// src/components/home/FeaturedCars.tsx
import { useEffect, useState, useRef } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import CarCard from '../car/CarCard';
import { Car } from '../../types/Car';

/* ---------- ANIMATED WORD ---------- */

function AnimatedWord({ words }: { words: string[] }) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIndex(i => (i + 1) % words.length);
        setVisible(true);
      }, 200);
    }, 1200);
    return () => clearInterval(interval);
  }, [words]);

  return (
    <>
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideDown {
          from { opacity: 1; transform: translateY(0); }
          to   { opacity: 0; transform: translateY(-8px); }
        }
        .aw-in  { animation: slideUp   0.3s ease forwards; }
        .aw-out { animation: slideDown 0.3s ease forwards; }
        .search-input:focus { outline: none; }
        .search-input::placeholder { color: transparent; }
      `}</style>
      <span
        className={visible ? 'aw-in' : 'aw-out'}
        style={{
          color: '#FFD700',
          fontWeight: 700,
          minWidth: '48px',
          display: 'inline-block',
        }}
      >
        {words[index]}
      </span>
    </>
  );
}

/* ---------- CUSTOM SELECT ---------- */

interface SelectOption { label: string; value: string; }

function CustomSelect({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: SelectOption[];
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find(o => o.value === value);

  return (
    <div ref={ref} style={{ position: 'relative', userSelect: 'none' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          padding: '12px 40px 12px 14px',
          borderRadius: '10px',
          border: `1px solid ${open ? '#FFD700' : '#333'}`,
          background: '#1a1a1a',
          color: selected ? '#fff' : '#666',
          cursor: 'pointer',
          fontSize: '14px',
          position: 'relative',
          transition: 'border-color 0.2s',
          whiteSpace: 'nowrap',
        }}
      >
        {selected ? selected.label : placeholder}
        <span style={{
          position: 'absolute', right: '12px', top: '50%',
          transform: `translateY(-50%) rotate(${open ? '180deg' : '0deg'})`,
          transition: 'transform 0.2s',
          display: 'flex', alignItems: 'center', pointerEvents: 'none',
        }}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 4L6 8L10 4" stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
          background: '#1a1a1a', border: '1px solid #333', borderRadius: '10px',
          overflow: 'hidden', zIndex: 100, boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
        }}>
          {options.map((opt, i) => (
            <div
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              style={{
                padding: '11px 14px', fontSize: '14px', cursor: 'pointer',
                color: opt.value === value ? '#FFD700' : '#ccc',
                background: opt.value === value ? '#222' : 'transparent',
                borderBottom: i < options.length - 1 ? '1px solid #222' : 'none',
                transition: 'background 0.15s, color 0.15s',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLDivElement).style.background = '#252525';
                if (opt.value !== value) (e.currentTarget as HTMLDivElement).style.color = '#fff';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLDivElement).style.background = opt.value === value ? '#222' : 'transparent';
                (e.currentTarget as HTMLDivElement).style.color = opt.value === value ? '#FFD700' : '#ccc';
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- MAIN COMPONENT ---------- */

export default function FeaturedCars() {
  const [cars, setCars] = useState<Car[]>([]);
  const [filteredCars, setFilteredCars] = useState<Car[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMake, setFilterMake] = useState('');
  const [filterFuel, setFilterFuel] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "cars"));
        const carsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as any[];
        const mappedCars: Car[] = carsData.map((car: any) => ({
          id: car.id,
          name: car.name || `${car.make || ''} ${car.model || ''}`.trim() || "Unknown Car",
          make: car.make || "Unknown",
          model: car.model || "",
          year: car.year || "",
          veh_price: typeof car.veh_price === 'string'
            ? parseInt(car.veh_price.replace(/[^0-9]/g, '')) || 0
            : (typeof car.veh_price === 'number' ? car.veh_price : 0),
          img: car.img || 'https://via.placeholder.com/600x400',
          fuel: car.veh_fuel_type || car.fuel || "Petrol",
          transmission: car.veh_transmission_type || car.transmission || "Manual",
          kmsDriven: car.veh_kms_driven || car.kmsDriven || "0",
          ownership: car.veh_ownership || car.ownership || "First",
          reg_number: car.reg_number || "",
        }));
        setCars(mappedCars);
        setFilteredCars(mappedCars);
      } catch (error) {
        console.error("Error fetching cars:", error);
      }
    };
    fetchCars();
  }, []);

  useEffect(() => {
    let result = [...cars];
  
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(car => {
        const year   = String(car.year ?? '').toLowerCase();
        const make   = (car.make ?? '').toLowerCase();
        const model  = (car.model ?? '').toLowerCase();
        const name   = (car.name ?? '').toLowerCase();
        const fuel   = (car.fuel ?? '').toLowerCase();
  
        return (
          name.includes(term)  ||
          make.includes(term)  ||
          model.includes(term) ||
          year.includes(term)  ||
          fuel.includes(term)  ||
          // match "make model" or "year make" combos e.g. "2022 swift"
          `${year} ${make}`.includes(term)   ||
          `${year} ${model}`.includes(term)  ||
          `${make} ${model}`.includes(term)
        );
      });
    }
  
    if (filterMake) result = result.filter(car => car.make.toLowerCase() === filterMake.toLowerCase());
    if (filterFuel) result = result.filter(car => car.fuel.toLowerCase() === filterFuel.toLowerCase());
  
    setFilteredCars(result);
  }, [searchTerm, filterMake, filterFuel, cars]);

  const fuelOptions: SelectOption[] = [
    { label: 'All Fuel', value: '' },
    { label: 'Petrol', value: 'Petrol' },
    { label: 'Diesel', value: 'Diesel' },
    { label: 'Hybrid', value: 'Hybrid' },
  ];

  return (
    <section id="featured-cars" style={section}>
      <div style={container}>
        <div style={header}>
          <div>
            <div style={tag}>FEATURED LISTINGS</div>
            <h2 style={title}>Hand-Picked Vehicles</h2>
          </div>
          <p style={desc}>Every car is inspected, cleaned, and road-ready.</p>
        </div>

        <div style={searchCard}>
          <div style={filtersRow}>

            {/* ── Search Input with animated placeholder ── */}
            <div style={{ position: 'relative' }}>

              {/* Search icon */}
              <div style={{
                position: 'absolute', left: '13px', top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex', alignItems: 'center',
                pointerEvents: 'none', zIndex: 1,
              }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <circle cx="11" cy="11" r="7"
                    stroke={isFocused ? '#FFD700' : '#555'} strokeWidth="2"
                    style={{ transition: 'stroke 0.2s' }}/>
                  <path d="M16.5 16.5L21 21"
                    stroke={isFocused ? '#FFD700' : '#555'} strokeWidth="2"
                    strokeLinecap="round" style={{ transition: 'stroke 0.2s' }}/>
                </svg>
              </div>

              {/* Animated placeholder — visible when idle and empty */}
              {!searchTerm && !isFocused && (
                <div style={{
                  position: 'absolute', left: '38px', top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  pointerEvents: 'none', zIndex: 1,
                  fontSize: '14px',
                  overflow: 'hidden',
                }}>
                  <span style={{ color: '#555' }}>Search by</span>
                  <AnimatedWord words={['Make', 'Model', 'Year']} />
                </div>
              )}

              {/* Static hint when focused and empty */}
              {!searchTerm && isFocused && (
                <div style={{
                  position: 'absolute', left: '38px', top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none', zIndex: 1,
                  fontSize: '14px', color: '#444',
                }}>
                  
                </div>
              )}

              <input
                type="text"
                className="search-input"
                placeholder=""
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                style={{
                  ...inputStyle,
                  paddingLeft: '38px',
                  paddingRight: searchTerm ? '36px' : '12px',
                  caretColor: '#FFD700',
                  border: `1px solid ${isFocused ? '#FFD700' : '#333'}`,
                  boxShadow: isFocused ? '0 0 0 3px rgba(255,215,0,0.08)' : 'none',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  width: '100%',
                }}
              />

              {/* Clear button */}
              {searchTerm && (
                <div
                  onClick={() => setSearchTerm('')}
                  style={{
                    position: 'absolute', right: '12px', top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                    padding: '2px',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="#555" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
              )}
            </div>

            <CustomSelect
              value={filterFuel}
              onChange={setFilterFuel}
              options={fuelOptions}
              placeholder="All Fuel"
            />
          </div>
        </div>

        <div style={grid}>
          {filteredCars.length > 0 ? (
            filteredCars.map((car) => <CarCard key={car.id} car={car} />)
          ) : (
            <div style={noData}>No cars found. Try adjusting filters.</div>
          )}
        </div>
      </div>
    </section>
  );
}

/* ---------- STYLES ---------- */

const section = { background: '#0a0a0a', padding: '40px 12px' };
const container = { maxWidth: '1200px', margin: '0 auto' };
const header = { display: 'flex', flexDirection: 'column' as const, gap: '10px', marginBottom: '30px' };
const tag = { color: '#FFD700', fontSize: '12px', fontWeight: 700 };
const title = { fontSize: '24px', fontWeight: 700 };
const desc = { color: '#aaa', fontSize: '13px' };
const searchCard = { background: '#111', border: '1px solid #222', borderRadius: '16px', padding: '16px', marginBottom: '20px' };
const filtersRow = { display: 'flex', flexDirection: 'column' as const, gap: '10px' };
const inputStyle = { padding: '12px', borderRadius: '10px', border: '1px solid #333', background: '#1a1a1a', color: '#fff', fontSize: '14px', outline: 'none' };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' };
const noData = { color: '#aaa', textAlign: 'center' as const, padding: '40px' };